import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../Layout';
import Plot from 'react-plotly.js';
import { listBaskets, getCompetitorData, parseCsvFile, fetchCompetitorData } from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';

/* ──────────────────────────────────────────────── */
/* Ranking Evolution — port of ranking_evolution.py */
/* BSR rank evolution with inverse Y-axis           */
/* ──────────────────────────────────────────────── */

function RankingEvolution() {
    const [baskets, setBaskets] = useState([]);
    const [selectedBasket, setSelectedBasket] = useState(null);
    const [data, setData] = useState([]);
    const [period, setPeriod] = useState('week');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [csvName, setCsvName] = useState(null);

    // ── Advanced Filters ──
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [brandsInitialized, setBrandsInitialized] = useState(false);
    const [dateFrom, setDateFrom] = useState('2026-01-01');
    const [dateTo, setDateTo] = useState('');

    const accountId = localStorage.getItem('selectedAccountValue') || '';

    const loadBaskets = useCallback(async () => {
        if (!accountId) return;
        try { const res = await listBaskets(accountId); setBaskets(res.data || []); } catch { }
    }, [accountId]);

    useEffect(() => { loadBaskets(); }, [loadBaskets]);

    const loadData = useCallback(async () => {
        if (!selectedBasket || !accountId || selectedBasket.id === 'csv') return;
        setLoading(true);
        setError('');
        try { const res = await getCompetitorData(selectedBasket.id, accountId); setData(res.data || []); }
        catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to load ranking data.');
        } finally { setLoading(false); }
    }, [selectedBasket, accountId]);

    useEffect(() => { loadData(); }, [loadData]);

    // CSV upload
    const handleCsvUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true);
        setError('');
        try {
            const res = await parseCsvFile(file);
            setData(res.data || []);
            setCsvName(file.name);
            setSelectedBasket({ id: 'csv', name: file.name });
        } catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message || 'Error parsing CSV.');
        }
        finally { setLoading(false); }
    };
    const handleClearCsv = () => { setCsvName(null); setSelectedBasket(null); setData([]); };

    const handleFetchData = async () => {
        if (!selectedBasket || selectedBasket.id === 'csv') return;
        if (!accountId) {
            setError('Select an Amazon Ads account before fetching data.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            await fetchCompetitorData(selectedBasket.id, accountId);
            const refreshed = await getCompetitorData(selectedBasket.id, accountId);
            setData(refreshed.data || []);
        } catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to fetch data.');
        } finally {
            setLoading(false);
        }
    };

    const groupKey = period === 'day' ? 'date' : (period === 'month' ? 'month' : 'week');

    const allBrands = useMemo(() => [...new Set(data.map(r => r.brand).filter(Boolean))].sort(), [data]);
    const allYears = useMemo(() => [...new Set(data.map(r => r.date ? String(r.date).substring(0, 4) : null).filter(Boolean))].sort(), [data]);

    useEffect(() => {
        if (allBrands.length > 0 && !brandsInitialized) {
            setSelectedBrands(allBrands);
            setBrandsInitialized(true);
        }
    }, [allBrands, brandsInitialized]);

    useEffect(() => {
        setBrandsInitialized(false);
        setSelectedBrands([]);
        setDateFrom('2026-01-01');
        setDateTo('');
    }, [selectedBasket]);

    const handleBrandToggle = (brand) => {
        setSelectedBrands(prev => prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]);
    };

    const filteredData = useMemo(() => {
        if (selectedBrands.length === 0) return [];
        const baseFiltered = data.filter(r => {
            if (r.brand && !selectedBrands.includes(r.brand)) return false;
            if (dateFrom && r.date && r.date < dateFrom) return false;
            if (dateTo && r.date && r.date > dateTo) return false;
            return true;
        }).map(r => ({
            ...r,
            month: r.date ? r.date.substring(0, 7) : ''
        }));

        const map = {};
        baseFiltered.forEach(r => {
            if (!r.date || !r.asin) return;
            const key = `${r.asin}_${r.date}`;
            const existing = map[key];
            const isJS = String(r.data_source || '').toLowerCase().includes('jungle');
            if (!existing) {
                map[key] = r;
            } else {
                const existingIsJS = String(existing.data_source || '').toLowerCase().includes('jungle');
                if (isJS || (!isJS && !existingIsJS)) {
                    map[key] = r;
                }
            }
        });
        return Object.values(map).sort((a, b) => new Date(a.date) - new Date(b.date));
    }, [data, selectedBrands, dateFrom, dateTo]);

    // Weeks/dates where at least one ASIN has rank_value data
    const activePeriods = useMemo(() => {
        return [...new Set(
            filteredData.filter(r => r.rank_value != null && r[groupKey]).map(r => r[groupKey])
        )].sort((a, b) => period === 'week' ? Number(a) - Number(b) : String(a).localeCompare(String(b)));
    }, [filteredData, groupKey, period]);

    const periodLabels = useMemo(() => {
        const labels = {};
        filteredData.forEach(r => {
            const key = r[groupKey];
            if (key && !labels[key] && r.date) {
                if (period === 'week') {
                    const d = new Date(r.date);
                    const localMonth = d.toLocaleString('en-US', { month: 'short' });
                    labels[key] = `${localMonth} ${d.getUTCDate()}`;
                } else if (period === 'month') {
                    const d = new Date(r.date);
                    const localMonth = d.toLocaleString('en-US', { month: 'short' });
                    labels[key] = `${localMonth} ${d.getUTCFullYear()}`;
                } else {
                    labels[key] = r.date;
                }
            }
        });
        return labels;
    }, [filteredData, groupKey, period]);

    // Only ASINs with at least 2 data points (need ≥2 to form a trend line)
    const asins = useMemo(() => {
        return [...new Set(filteredData.map(r => r.asin).filter(Boolean))].filter(asin => {
            const count = filteredData.filter(r => r.asin === asin && r.rank_value != null).length;
            return count >= 2;
        });
    }, [filteredData]);

    const buildTraces = () => {
        return asins.map(asin => {
            const rows = filteredData.filter(r => r.asin === asin && r.rank_value != null).sort((a, b) => new Date(a.date) - new Date(b.date));
            const brand = rows[0]?.brand || 'Unknown';
            return {
                x: rows.map(r => r[groupKey]),
                y: rows.map(r => r.rank_value),
                mode: 'lines+markers',
                name: `${brand} - ${asin}`,
                hovertemplate: `ASIN: ${asin}<br>Rank: #%{y:,}<br>Category: %{customdata[0]}<br>Source: %{customdata[1]}<extra></extra>`,
                customdata: rows.map(r => [r.sub_category_name || '—', r.data_source || 'RapidAPI']),
                connectgaps: false,
            };
        });
    };

    const providerHint = useMemo(() => {
        if (!error) return '';
        const normalized = String(error).toLowerCase();
        if (normalized.includes('not subscribed') || normalized.includes('403')) {
            return 'Live provider access is restricted or not subscribed (Error 403). Please verify your RapidAPI subscription or use a historical tracking CSV.';
        }
        if (normalized.includes('too many requests') || normalized.includes('429')) {
            return 'Provider rate limit reached (Error 429). Retry in a few minutes or reduce ASIN volume.';
        }
        if (normalized.includes('external provider failed')) {
            return 'External provider failed for this request. Try again later or use a historical CSV.';
        }
        return '';
    }, [error]);

    return (
        <Layout>
            <div className="container-fluid py-4">
                <h2 className="fw-bold mb-4">BSR Ranking Evolution</h2>

                {error && <div className="alert alert-danger">{error}</div>}
                {providerHint && <div className="alert alert-warning">{providerHint}</div>}

                <div className="row mb-3">
                    <div className="col-md-4">
                        <select className="form-select" value={selectedBasket?.id || ''}
                            onChange={e => setSelectedBasket(baskets.find(b => b.id === parseInt(e.target.value)) || null)}>
                            <option value="">Select basket...</option>
                            {baskets.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <div className="d-flex align-items-center gap-2">
                            <label className="btn btn-outline-info btn-sm mb-0" style={{ cursor: 'pointer' }}>
                                <i className="bi bi-filetype-csv me-1"></i>Upload CSV
                                <input type="file" accept=".csv" hidden onChange={handleCsvUpload} />
                            </label>
                            {csvName && (
                                <span className="badge bg-success d-flex align-items-center gap-1">
                                    {csvName}
                                    <i className="bi bi-x-lg" style={{ cursor: 'pointer' }} onClick={handleClearCsv}></i>
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="btn-group btn-group-sm w-100">
                            <button className={`btn ${period === 'month' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPeriod('month')}>Monthly</button>
                            <button className={`btn ${period === 'week' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPeriod('week')}>Weekly</button>
                            <button className={`btn ${period === 'day' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPeriod('day')}>Daily</button>
                        </div>
                    </div>
                    <div className="col-md-2">
                        <button
                            className="btn btn-success btn-sm w-100"
                            onClick={handleFetchData}
                            disabled={loading || !selectedBasket || selectedBasket.id === 'csv'}
                            title="Fetch latest competitor data"
                        >
                            Fetch Data
                        </button>
                    </div>
                </div>

                {loading && <div className="text-center"><div className="spinner-border" /></div>}

                {!loading && selectedBasket && data.length === 0 && !error && (
                    <div className="alert alert-warning">
                        No hay datos para mostrar. Debes ejecutar Fetch Data en Tracking Overview. Si el proveedor falla (403/429), no se generan nuevos datos.
                    </div>
                )}

                {!loading && data.length > 0 && (
                    <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                        <div className="card-header bg-white py-3 border-0 d-flex justify-content-between align-items-center">
                            <h6 className="fw-bold mb-0 text-slate-800"><i className="bi bi-funnel"></i> Advanced Filters</h6>
                            <span className="badge bg-primary bg-opacity-10 text-primary">Filtered ASINs: {asins.length}</span>
                        </div>
                        <div className="card-body pt-0">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-bold small text-muted mb-1">Brands to display</label>
                                    <div className="border rounded p-2" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                        <div className="d-flex gap-2 mb-2">
                                            <button className="btn btn-outline-secondary btn-sm py-0 px-2" style={{ fontSize: '0.75rem' }} onClick={() => setSelectedBrands([...allBrands])}>All</button>
                                            <button className="btn btn-outline-secondary btn-sm py-0 px-2" style={{ fontSize: '0.75rem' }} onClick={() => setSelectedBrands([])}>None</button>
                                        </div>
                                        {allBrands.map(b => (
                                            <div key={b} className="form-check form-check-sm mb-1">
                                                <input className="form-check-input" type="checkbox" id={`flt-brand-${b}`}
                                                    checked={selectedBrands.includes(b)}
                                                    onChange={() => handleBrandToggle(b)} />
                                                <label className="form-check-label small" htmlFor={`flt-brand-${b}`}>{b}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-bold small text-muted mb-1">Date Range</label>
                                    <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                                        <button className={`btn btn-sm py-1 px-2 ${(!dateFrom && !dateTo) ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => { setDateFrom(''); setDateTo(''); }}>All</button>
                                        {allYears.map(year => (
                                            <button key={year} className={`btn btn-sm py-1 px-2 ${(dateFrom === `${year}-01-01` && dateTo === `${year}-12-31`) ? 'btn-primary' : 'btn-outline-secondary'}`} 
                                                onClick={() => { setDateFrom(`${year}-01-01`); setDateTo(`${year}-12-31`); }}>{year}</button>
                                        ))}
                                    </div>
                                    <div className="d-flex gap-2">
                                        <input type="date" className="form-control form-control-sm" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                                        <span className="text-muted">to</span>
                                        <input type="date" className="form-control form-control-sm" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {filteredData.length === 0 && data.length > 0 && (
                    <div className="alert alert-info border-0 shadow-sm">No data matches the current filter criteria.</div>
                )}

                {filteredData.length > 0 && (
                    <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                        <div className="card-body">
                            <Plot
                                data={buildTraces()}
                                layout={{
                                    title: 'Best Sellers Rank Evolution',
                                    height: 500,
                                    xaxis: {
                                        title: { text: period === 'day' ? 'Date' : (period === 'month' ? 'Month' : 'Week') },
                                        ...(period === 'day'
                                            ? { type: 'date', tickformat: '%b %d', range: activePeriods.length ? [activePeriods[0], activePeriods[activePeriods.length - 1]] : undefined }
                                            : { type: 'category', categoryorder: 'array', categoryarray: activePeriods.map(String), tickmode: 'array', tickvals: activePeriods.map(String), ticktext: activePeriods.map(p => periodLabels[p] || p) }),
                                    },
                                    yaxis: {
                                        title: { text: 'BSR Rank' },
                                        autorange: 'reversed',
                                    },
                                    margin: { l: 65, r: 20, t: 50, b: 55 },
                                    hovermode: 'closest',
                                }}
                                config={{ responsive: true }}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                )}

                {/* ═══ Breakdown by ASIN — individual subplots grid ═══ */}
                {filteredData.length > 0 && asins.length > 0 && (
                    <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                        <div className="card-body">
                            <h5 className="mb-3">Ranking Evolution (by ASIN)</h5>
                            <div className="row">
                                {asins.map(asin => {
                                    const asinData = filteredData.filter(r => r.asin === asin && r.rank_value != null).sort((a, b) => new Date(a.date) - new Date(b.date));
                                    const brand = asinData[0]?.brand || 'Unknown';
                                    if (asinData.length < 2) return null;

                                    const asinPeriods = asinData.map(r => r[groupKey]);

                                    return (
                                        <div className="col-md-4 mb-3" key={asin}>
                                            <Plot
                                                data={[{
                                                    x: asinData.map(r => r[groupKey]),
                                                    y: asinData.map(r => r.rank_value),
                                                    mode: 'lines+markers',
                                                    line: { width: 2 },
                                                    marker: { size: 6 },
                                                    hovertemplate: `Rank: #%{y:,}<br>Source: %{customdata}<extra></extra>`,
                                                    customdata: asinData.map(r => r.data_source || 'RapidAPI'),
                                                    connectgaps: false,
                                                }]}
                                                layout={{
                                                    title: { text: `${brand} - ${asin}`, font: { size: 13, color: '#000' } },
                                                    height: 250,
                                                    xaxis: {
                                                        title: { text: period === 'day' ? 'Date' : (period === 'month' ? 'Month' : 'Week') },
                                                        showgrid: false,
                                                        ...(period === 'day'
                                                            ? { type: 'date', tickformat: '%b %d' }
                                                            : { type: 'category', categoryorder: 'array', categoryarray: asinPeriods.map(String), tickmode: 'array', tickvals: asinPeriods.map(String), ticktext: asinPeriods.map(p => periodLabels[p] || p) }),
                                                    },
                                                    yaxis: { title: { text: 'Rank' }, autorange: 'reversed', showgrid: true },
                                                    margin: { l: 60, r: 15, t: 35, b: 50 },
                                                }}
                                                config={{ responsive: true, displayModeBar: false }}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Pivot table (Ranking History) */}
                {(() => {
                    const buildPivotTable = () => {
                        const keys = [...new Set(
                            filteredData.filter(r => r[groupKey] && r.rank_value != null).map(r => r[groupKey])
                        )].sort((a, b) => period === 'week' ? Number(a) - Number(b) : String(a).localeCompare(String(b)));
                        const rows = keys.map(key => {
                            const row = { [groupKey]: key };
                            asins.forEach(asin => {
                                const match = filteredData.find(r => r.asin === asin && r[groupKey] === key);
                                row[`${asin}`] = match?.rank_value != null ? Number(match.rank_value).toLocaleString() : '—';
                            });
                            return row;
                        });
                        return rows;
                    };
                    const pivotData = filteredData.length > 0 ? buildPivotTable() : [];
                    const pivotColumns = pivotData.length > 0 ? Object.keys(pivotData[0]) : [];

                    return pivotData.length > 0 && (
                        <div className="card mb-4">
                            <div className="card-header" id="headingHistory">
                                <h5 className="mb-0">
                                    <button className="btn btn-link text-decoration-none" data-bs-toggle="collapse" data-bs-target="#collapseHistory">
                                        Show ranking table
                                    </button>
                                </h5>
                            </div>
                            <div id="collapseHistory" className="collapse">
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table table-sm table-striped table-hover text-center">
                                            <thead className="table-dark">
                                                <tr>{pivotColumns.map(c => <th key={c}>{c}</th>)}</tr>
                                            </thead>
                                            <tbody>
                                                {pivotData.map((row, i) => (
                                                    <tr key={i}>{pivotColumns.map(c => <td key={c}>{row[c]}</td>)}</tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Current rankings table */}
                {filteredData.length > 0 && asins.length > 0 && (
                    <div className="card mt-4 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                        <div className="card-body">
                            <h5>Current Rankings</h5>
                            <div className="table-responsive">
                                <table className="table table-sm table-striped">
                                    <thead className="table-light">
                                        <tr><th>ASIN</th><th>Brand</th><th>Rank</th><th>Sub-category</th></tr>
                                    </thead>
                                    <tbody>
                                        {asins.map(asin => {
                                            const latest = filteredData.filter(r => r.asin === asin).sort((a, b) => new Date(b.date) - new Date(a.date))[0];
                                            return (
                                                <tr key={asin}>
                                                    <td>{asin}</td>
                                                    <td>{latest?.brand || '—'}</td>
                                                    <td>{latest?.rank_value != null ? `#${latest.rank_value.toLocaleString()}` : '—'}</td>
                                                    <td>{latest?.sub_category_name || '—'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default RankingEvolution;
